import * as motion from "motion/react-client"
import { Link } from "react-router-dom";
import StickyNavbar from "../navbar";

const SectionThree = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  return (
    <div>
      <StickyNavbar />
      <motion.div
        className="bg-white space-y-5 text-black p-4 lg:mt-10 mt-20 sm:p-10 lg:px-20 text-lg"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        {/* Who takes part in research? */}
        <motion.div className="space-y-3" variants={fadeIn}>
          <motion.h2
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center capitalize"
            variants={fadeIn}
          >
            Who takes part in research?
          </motion.h2>
          <motion.p variants={fadeIn}>
          Research can involve people from many different backgrounds. The people taking part, called research participants, will depend on the focus of the research and the kinds of views you want to gather. Sometimes, research may bring together multiple groups and this helps to give a well-rounded picture (26-28).
          </motion.p>
          <motion.div className=" rounded-lg p-4" variants={fadeIn}>
            <div className="flex justify-center">
              <motion.img
                src="/images/participants.png"
                alt="Local leaders supporting research"
                className="w-full max-w-md rounded-md"
                variants={fadeIn}
              />
            </div>
            <p className="text-center text-sm mt-2">
              Setting up the Participatory Action Research process...
            </p>
          </motion.div>
          <motion.ul
            className="list-disc list-inside"
            variants={staggerContainer}
          >
            <motion.li variants={fadeIn}>
              Patients: Patients can share firsthand experiences on health
              conditions...
            </motion.li>
            <motion.li variants={fadeIn}>
              Community members: As participants, community members can give us
              valuable information...
            </motion.li>
            <motion.li variants={fadeIn}>
              Caregivers and family members: Family members and caregivers...
            </motion.li>
          </motion.ul>
          <motion.p variants={fadeIn}>
          As you can see, many different people can take part in research. However, it’s important that everyone does so willingly, with a full understanding of the research being done—this is known as voluntary participation and giving informed consent.
          </motion.p>
        </motion.div>

        {/* Where should research be done? */}
        <motion.div className="space-y-3" variants={fadeIn}>
          <motion.h2
            className="text-xl font-bold mb-2 text-[#005FFF]"
            variants={fadeIn}
          >
            Where should research be done?
          </motion.h2>
          <motion.ul
            className="list-decimal list-inside"
            variants={staggerContainer}
          >
            <motion.li variants={fadeIn}>Anywhere and everywhere</motion.li>
            <motion.li variants={fadeIn}>Various settings</motion.li>
          </motion.ul>
          <motion.ul
            className="list-disc list-inside"
            variants={staggerContainer}
          >
            <motion.li variants={fadeIn}>Schools</motion.li>
            <motion.li variants={fadeIn}>Hospitals</motion.li>
            <motion.li variants={fadeIn}>Communities</motion.li>
            <motion.li variants={fadeIn}>Homes</motion.li>
            <motion.li variants={fadeIn}>Church</motion.li>
          </motion.ul>
        </motion.div>

        {/* Research can happen in many places */}
        <motion.div className="space-y-3" variants={fadeIn}>
          <motion.h2 className="text-xl text-[#005FFF] font-bold mb-2" variants={fadeIn}>
            Research can happen in many places:
          </motion.h2>
          <motion.ul
            className="list-disc list-inside space-y-3"
            variants={staggerContainer}
          >
            <motion.li variants={fadeIn}>
             By doing research in community health centres and health clinics, we can learn from talking to patients, giving health education, or collecting feedback from people who work there. Research in these settings helps us understand if certain methods are working well and helps us to spot areas for improvement. (29-32)
            </motion.li>
            <motion.li variants={fadeIn}>
            Research in schools and other places of education can involve gathering information on health issues that affect children and young people (33).
            </motion.li>
            <motion.li variants={fadeIn}>
             Visiting homes offers a great chance to do important research, such as collect information about household relationships or living conditions that might impact health. If some participants are unable to travel for the research, this can also be a good option (34-36).
            </motion.li>
            <motion.li variants={fadeIn}>
            Doing research in faith-based centres, such as churches and mosques, helps build trust and engage with community members in a familiar environment, making it easier to discuss health needs, and cultural, faith based beliefs that may affect health (37-39).
            </motion.li>
          </motion.ul>
        </motion.div>

        {/* When should research be done? */}
        <motion.div className="space-y-3" variants={fadeIn}>
          <motion.h2
            className="text-xl font-bold mb-2 text-[#005FFF]"
            variants={fadeIn}
          >
            When should research be done?
          </motion.h2>
          <motion.p variants={fadeIn}>
          Research can be done at different times, depending on the needs and goals of the community. Here are some situations where it’s useful to do research:
          </motion.p>
          <motion.ul
            className="list-disc list-inside space-y-3"
            variants={staggerContainer}
          >
            <motion.li variants={fadeIn}>
            In response to a health concern: When a health issue starts affecting many people, research can help understand the issue and find ways to manage it. For example, during the COVID-19 pandemic, researchers quickly began studies to understand the virus, reduce its impact, and develop a vaccine(40-42).
            </motion.li>
            <motion.li variants={fadeIn}>
            To improve current practices: Checking on practices regularly can show us whether they’re working well and what we can do to make health care better. For example, we can do research to learn about how happy patients are with the level of care, and find out different ways to improve this satisfaction (43-45)
            </motion.li>
            <motion.li variants={fadeIn}>
            To prepare for future needs: Research can help us plan for health needs, such as by understanding how seasonal and climate changes affect the community. For example, during times of the year when healthy foods are less available, we can use research to plan health programs and put them in place at the right times. This allows us to better support community health throughout the year.(46-48)
            </motion.li>
          </motion.ul>
        </motion.div>

        {/* Navigation Buttons */}
        <motion.div className="flex justify-between mt-10" variants={fadeIn}>
          <Link to="/chapter1/SectionTwo">
            <motion.button
              className="bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
              whileHover={{ scale: 1.1 }}
            >
              Previous
            </motion.button>
          </Link>
          <Link to="/chapter1/sectionFour">
            <motion.button
              className="bg-blue-900 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
              whileHover={{ scale: 1.1 }}
            >
              Next
            </motion.button>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SectionThree;
